import { useCallback, useEffect, useMemo, useState } from 'react'
import { Trans } from '@lingui/react/macro'
import { Box, Button, Flex, useBreakpointValue, Text } from '@chakra-ui/react'
import { useDispatch, useSelector } from 'react-redux'

import { useRushBSkins } from '../hooks/useRushBSkins'
import useRushExchange from '../hooks/useRushExchange'
import { RushSkin } from '../types/rush'
import PopupModal from './Modal'
import LoadMoreIcon from './Icons/LoadMore'
import Loading from './Loading'
import { colors } from '../theme/cssVariables'
import SkinCardInfo from './SkinCardInfo'
import {
  exteriorType,
  getRarityColorMap,
  rarityType,
  specialtyType,
} from '../constants/skinPropertes'

import RushSellButton from './RushSellButton'
import RushSkinCard from './RushSkinCard'
import RushPrice from './RushPrice'
import { selectIsOpenRushSkinsModal } from '../store/rushbSelectors'
import { setIsOpenRushSkinsModal } from '../store/rushbSlice'

const DEFAULT_PAGE_SIZE = 20
const RushSkinsModal = () => {
  const isOpen = useSelector(selectIsOpenRushSkinsModal)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const { skins, hasMoreSkins, fetchNextSkins, isLoadingNextPage, loading, refetchSkins } =
    useRushBSkins({ pageSize })
  const { exchangeSkins, skinLoading } = useRushExchange()
  const [checkedSkins, setCheckedSkins] = useState<RushSkin[]>([])

  const dispatch = useDispatch()
  const onClose = useCallback(() => {
    dispatch(setIsOpenRushSkinsModal(false))
  }, [dispatch])

  useEffect(() => {
    if (isOpen) {
      refetchSkins()
    }
  }, [isOpen, refetchSkins])

  // 选中皮肤
  const handleCheck = (skin: RushSkin) => {
    if (checkedSkins.filter((s) => s.instanceId === skin.instanceId).length > 0) {
      setCheckedSkins((prev) => prev.filter((s) => s.instanceId !== skin.instanceId))
    } else {
      setCheckedSkins((prev) => [...prev, skin])
    }
  }
  const [isSellAllLoading, setIsSellAllLoading] = useState(false)

  const totalPrice = useMemo(() => {
    return checkedSkins.reduce(
      (acc, skin) => acc + (skins.find((s) => s.instanceId === skin.instanceId)?.value ?? 0),
      0,
    )
  }, [checkedSkins, skins])
  const refreshSkinAfterAction = useCallback(async () => {
    if (skins.length - 1 <= DEFAULT_PAGE_SIZE) {
      await refetchSkins()
    } else {
      setPageSize(Math.max(skins.length - 1, DEFAULT_PAGE_SIZE))
    }
  }, [refetchSkins, skins.length])
  const handleMultipleSell = async () => {
    if (checkedSkins.length === 0) return
    setIsSellAllLoading(true)

    const res = await exchangeSkins({
      instanceIds: Array.from(new Set(checkedSkins.map((skin) => skin.instanceId))),
      isBonus: false,
    })
    if (!res) return
    await refreshSkinAfterAction()
    setCheckedSkins([])
    setIsSellAllLoading(false)
  }

  const handleSingleSell = async (skin: RushSkin) => {
    const res = await exchangeSkins({
      instanceIds: [skin.instanceId],
      isBonus: false,
    })
    if (!res) return
    await refreshSkinAfterAction()
    setCheckedSkins((prev) => prev.filter((s) => s.instanceId !== skin.instanceId))
  }

  const cardWidth = useBreakpointValue({
    base: 250,
    sm: 250,
    md: 280,
  })

  const handleSelectAll = () => {
    setCheckedSkins(skins)
  }
  const handleOnCancel = () => {
    if (checkedSkins.length === skins.length) setCheckedSkins([])
    else {
      setCheckedSkins([])
      onClose()
    }
  }
  return (
    <PopupModal
      titleComponent={
        <Flex width={'100%'} justifyContent={'center'}>
          <Text>
            <Trans>SELECT ITEMS</Trans>
          </Text>
        </Flex>
      }
      isOpen={isOpen}
      onClose={onClose}
      size={['full', '6xl']}
    >
      {loading && !skins?.length ? (
        <Flex justifyContent={'center'} alignItems={'center'} height={'70vh'} width={'100%'}>
          <Loading />
        </Flex>
      ) : (
        <Box fontSize={['md']} width={'100%'}>
          <Flex
            py={2}
            gap={[5, 2]}
            direction={['column', 'row']}
            justifyContent={'space-between'}
            alignItems={'center'}
          >
            <Flex gap={2} alignItems={'center'}>
              <Trans>Active Items</Trans>
              <Flex gap={1}>
                <Text color={colors.colorA4}>{checkedSkins.length}</Text>
                <Text>
                  <Trans>items for</Trans>
                </Text>
                <RushPrice
                  price={totalPrice}
                  color={colors.colorA4}
                  fontSize={'sm'}
                  _groupHover={{ color: colors.colorB7 }}
                />
              </Flex>
            </Flex>
            <Flex gap={2}>
              <Button size={['sm']} variant={'ghost-gray'} gap={2} onClick={handleOnCancel}>
                <Trans>Cancel</Trans>
              </Button>
              {checkedSkins.length !== skins.length && (
                <Button
                  bg={colors.colorA1Alpha80}
                  color={colors.colorB7}
                  _hover={{ bg: colors.colorA1Alpha80 }}
                  size={['sm']}
                  gap={2}
                  cursor={'pointer'}
                  onClick={handleSelectAll}
                >
                  <Trans>Select All</Trans>
                </Button>
              )}
              <Button
                variant={'blueOutline'}
                isLoading={isSellAllLoading}
                size={['sm']}
                gap={2}
                isDisabled={checkedSkins.length === 0}
                onClick={handleMultipleSell}
              >
                {checkedSkins.length === skins.length ? (
                  <Trans>Sell All</Trans>
                ) : (
                  <Trans>Sell</Trans>
                )}
              </Button>
            </Flex>
          </Flex>
          {skins.length > 0 ? (
            <Box maxH={'70vh'} py={4} overflowY={'auto'}>
              <Flex justifyContent={'center'} flexWrap={'wrap'} gap={'20px'}>
                {skins?.map((skin) => {
                  return (
                    <RushSkinCard
                      skin={skin}
                      width={`${cardWidth}px`}
                      height={'auto'}
                      onClick={() => handleCheck(skin)}
                      borderColor={
                        checkedSkins.filter((v) => v.instanceId === skin.instanceId).length > 0
                          ? colors.colorA4
                          : colors.colorB5
                      }
                      key={skin.instanceId}
                    >
                      <SkinCardInfo
                        specialtyKey={skin.treasure?.specialty as unknown as specialtyType}
                        displayName={skin.treasure?.name || ''}
                        exteriorKey={skin.treasure?.exterior as unknown as exteriorType}
                      />
                      <RushPrice price={skin?.value} />
                      <RushSellButton
                        onClick={() => handleSingleSell(skin)}
                        mb={4}
                        isLoading={skinLoading?.includes(skin.instanceId)}
                      />
                    </RushSkinCard>
                  )
                })}
              </Flex>
              {hasMoreSkins && (
                <Flex width={'100%'} justify={'center'} mt={[4, 8]}>
                  <LoadMoreIcon onClick={fetchNextSkins} isLoading={isLoadingNextPage} />
                </Flex>
              )}
            </Box>
          ) : (
            <Flex
              px={`2px`}
              justifyContent={'center'}
              gap={[0, '30px 35px']}
              width={'100%'}
              flexWrap={'wrap'}
              justify={'center'}
              alignItems={'center'}
              height={'300px'}
              color={colors.colorB3}
            >
              <Trans>No Data</Trans>
            </Flex>
          )}
        </Box>
      )}
    </PopupModal>
  )
}

export default RushSkinsModal
